import { Img, Picture } from '@xo-union/tk-component-picture';
import { DataProxy } from 'apollo-cache';
import Honeybadger from 'honeybadger-js';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import RightRail from '../../../libs/gs-component-right-rail';

import { useAiTextGenerationContext } from '../../../context/AiTextGenerationContext';
import { useWebsiteLayoutContext } from '../../../context/WebsiteLayoutContext';
import {
  PagesFragment,
  UpdateThemeMutation,
  useDeletePageItemMutation,
  usePagesQuery,
  useUpdateThemeMutation,
  useWeddingWebsiteQuery,
  WeddingWebsiteQuery,
  Wws_Page,
} from '../../../generated/graphql';
import deletePageItem from '../../../helpers/apolloCache/deletePageItem';
import { getTargetOrigin } from '../../../helpers/getTargetOrigin';
import { reloadIframe } from '../../../helpers/iframeHelpers';
import {
  basicInfoRailRoute,
  onboardingDesignPLPRoute,
  pageItemTypeRoute,
  previewRoute,
  previewThemeRoute,
  previewThemeRouteMatch,
} from '../../../helpers/routes';
import useIsLiteSite from '../../../helpers/useIsLiteSite';
import useQueryString from '../../../helpers/useQueryString';
import useShouldPurchaseDomain from '../../../helpers/useShouldPurchaseDomain';
import { isMissingWeddingWebsiteError } from '../../../helpers/isMissingWeddingWebsiteError';
import { withCurrentQueryParams } from '../../../helpers/withCurrentQueryParams';
import useBuildThemeAnalyticsBaseProps from '../../../hooks/useBuildThemeAnalyticsBaseProps';
import { ConfirmationModal } from '../../../libs/gs-component-form';
import Loading from '../../../Loading';
import { PageCardUIContext } from '../../../PageCardUIContext';
import { TrackingContext } from '../../../TrackingContext';
import VariantContext from '../../../VariantContext';
import { BasicInfoRail } from '../../BasicInfoRail';
import { weddingWebsiteQuery } from '../../Dashboard';
import DoubleActionWithError from '../../FormComponents/DoubleActionWithError';
import { AddPage } from '../../ManagePagesRail/AddPage';
import { ReorderPages } from '../../ManagePagesRail/ReorderPages';
import { ManageRsvpSettingsRail } from '../../ManageRsvpSettingsRail';
import FullScreenModal from '../../Modals/FullScreenModal';
import { TextGenerationModal } from '../../Modals/TextGenerationModal';
import { RightRailContext } from '../../PageItemsRail';
import PageItemRoutes from '../../PageItemsRail/PageItemRoutes';
import { PageSettingsRail } from '../../PageSettingsRail';
import { useWorkflowUnstartedState } from '../../RsvpPageCard/utils';
import { SettingsRailContent } from '../../SettingsRail/SettingsRailContent';
import IndexWebsiteBanner from '../../SharedComponents/IndexWebsiteBanner/IndexWebsiteBanner';
import PublishWebsiteBanner from '../../SharedComponents/PublishWebsiteBanner/PublishWebsiteBanner';
import { selectedThemeQuery } from '../../ThemeSettings/graphql';
import { lookupTheme, lookupThemeFamily } from '../ChangeThemeRail';
import EditPageRail from '../EditPageRail';
import { Theme } from '../ThemeCard';
import ThemeInfo from '../ThemeInfo';
import DesignRail from './components/DesignRail/DesignRail';
import styles from './preview.scss';
import PreviewHeader from './PreviewHeader';
import PreviewRightRail from './PreviewRightRail';

export type PreviewState = 'mobile' | 'desktop' | 'paper';

export const updateThemeSuccessCallback = (
  cache: DataProxy,
  data: UpdateThemeMutation | null | undefined,
  newThemeId: number
) => {
  const { weddingWebsite, ...rest } = cache.readQuery<WeddingWebsiteQuery>({ query: weddingWebsiteQuery })!;
  const updatedWeddingWebsite = { ...weddingWebsite, themeId: newThemeId };
  cache.writeQuery({
    query: weddingWebsiteQuery,
    data: { weddingWebsite: updatedWeddingWebsite, ...rest },
  });
  cache.writeQuery({
    query: selectedThemeQuery,
    data: { theme: data?.updateSelectedTheme },
  });
};

const RSVP_PAGE = 'rsvp';

const Preview: React.FC = () => {
  // url hooks
  const history = useHistory();
  const match = useRouteMatch<{ themeId: string }>(previewThemeRouteMatch);
  const queryString = useQueryString();
  const urlTheme = match?.params.themeId ? parseInt(match.params.themeId) : null;
  const urlLocation = queryString.get('location');
  const railOpen = !!queryString.get('open');
  const openEditPageRail = !!queryString.get('open_edit');
  const mobile = !!queryString.get('mobile');

  // gql hooks
  const { data: wwsData, loading: wwsLoading, error: wwsError } = useWeddingWebsiteQuery();
  const [updateTheme, { loading: updatingTheme }] = useUpdateThemeMutation();
  const { data } = usePagesQuery();
  const pages = data?.pages || [];
  const shouldPurchaseDomain = useShouldPurchaseDomain(wwsData?.weddingWebsite?.purchaseDomain);
  const [deletePageItemMutation] = useDeletePageItemMutation();

  // Theme Data
  const { themes } = useContext(VariantContext);

  const { previewLayout, resetPreviewLayout } = useWebsiteLayoutContext();

  // custom hooks
  const liteSite = useIsLiteSite();
  const { getThemeAnalyticsBaseProps } = useBuildThemeAnalyticsBaseProps();

  const { isUnstartedState } = useWorkflowUnstartedState();

  const possibleLocationQueryParamValues = useMemo(
    () => pages?.map(({ routeName }) => routeName)?.filter(Boolean) || [],
    [pages]
  );

  // state hooks
  const [selectedPreview, setSelectedPreview] = useState<PreviewState>(mobile ? 'mobile' : 'desktop');
  const [rightRailOpen, setRightRailOpen] = useState(!!urlTheme);
  // default open rail on preview for: rsvp (page card experiment)
  const [editWebsiteOpen, setEditWebsiteOpen] = useState(!railOpen);
  const [previewLocation, setPreviewLocation] = useState(urlLocation || '');
  const [editPageOpen, setEditPageOpen] = useState(
    (urlLocation && possibleLocationQueryParamValues.includes(urlLocation)) || openEditPageRail
  );
  const [addPageOpen, setAddPageOpen] = useState<boolean>(false);
  const [settingsRailOpen, setSettingsRailOpen] = useState<boolean>(false);
  const [reorderPagesOpen, setReorderPagesOpen] = useState<boolean>(false);
  const [showRsvpEmptyState, setShowRsvpEmptyState] = useState<boolean>(false);
  const [designRailOpen, setDesignRailOpen] = useState<boolean>(false);
  const [changeInactiveThemeModalOpen, setChangeInactiveThemeModalOpen] = useState(false);
  const [isDeletingPageItem, setIsDeletingPageItem] = useState<boolean>(false);

  if (
    !wwsLoading &&
    (isMissingWeddingWebsiteError(wwsError) || wwsData?.weddingWebsite?.state === 'uninitialized') &&
    process.env.REACT_APP_REDIRECT_UNINITIALIZED_WWS === 'true'
  ) {
    // keep inbound marketing params (utm_*, gclid, ...) across the redirect
    window.location.href = withCurrentQueryParams(onboardingDesignPLPRoute, window.location.search);
  }

  const {
    modalOpen: textGenerationModalOpen,
    isThinking,
    errorMessage,
    isRetryableError,
    selectedOption,
    presetOptions,
    retryRequest,
    setModalOpen,
    generatedTextHistory,
    field,
    isModalInZeroState,
    activeHistoryPage,
  } = useAiTextGenerationContext();

  const getGeneratedText = useCallback((): string => {
    if (isModalInZeroState) {
      return field?.fieldText || '';
    }

    return generatedTextHistory && generatedTextHistory.length > 0
      ? generatedTextHistory[activeHistoryPage].generatedText
      : '';
  }, [generatedTextHistory, field, isModalInZeroState, activeHistoryPage]);

  // generated text to be the latest in history
  const generatedText = getGeneratedText();

  const getGeneratedTextModalTitle = useCallback(
    (selectedOption: string) => {
      if (isModalInZeroState) {
        return 'Custom Prompt';
      }
      //don't use the history if no history exists or we're on the latest page
      const presetOptionToLabelMap =
        isThinking || !generatedTextHistory || generatedTextHistory.length === activeHistoryPage
          ? selectedOption
          : generatedTextHistory?.[activeHistoryPage]?.presetOption;
      return presetOptions.find(option => option.value === presetOptionToLabelMap)?.label || 'Custom Prompt';
    },
    [presetOptions, isModalInZeroState, generatedTextHistory, activeHistoryPage, isThinking]
  );

  const generatedTextModalTitle = getGeneratedTextModalTitle(selectedOption || '');

  // set pseudo rsvp page when on rsvp
  const [currentPage, setCurrentPage] = useState<PagesFragment | Wws_Page | string | undefined>(() => {
    // don't show the RSVP preview page if its unstarted
    if (previewLocation === RSVP_PAGE && isUnstartedState) {
      setPreviewLocation('');
    }

    return previewLocation === RSVP_PAGE ? RSVP_PAGE : pages.find(p => p.routeName === previewLocation);
  });
  const [previewThemeId, setPreviewThemeId] = useState(urlTheme);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Look up the paper suite image and designer URL.
  const { paperSuiteImageId, designerUrl } = useMemo(() => {
    const theme = lookupTheme(previewThemeId || wwsData?.weddingWebsite?.themeId || 0, themes);
    return {
      paperSuiteImageId: theme?.stationaryPreviewImage?.mediaApiId,
      designerUrl: theme?.designerUrl || '',
    };
  }, [wwsData, themes, previewThemeId]);

  // Right Rail Context stuff
  const [overlayEnabled, setOverlayEnabled] = useState<boolean>(false);
  const [previewOnClose, setPreviewOnClose] = useState<boolean>(false);
  const [showPublishedIndexBanner, setShowPublishedIndexBanner] = useState<boolean>(false);

  const disableOverlay = () => setOverlayEnabled(false);
  const enableOverlay = () => setOverlayEnabled(true);
  const previewAfterSave = () => setPreviewOnClose(true);
  const closeAfterSave = () => setPreviewOnClose(false);

  // analytics
  const { track, setPagesLoaded } = useContext(TrackingContext);
  const { togglePageOpen } = useContext(PageCardUIContext);

  const resetThemeToCurrent = (closeRail: boolean = true) => {
    setPreviewThemeId(null);
  };

  const closeAllRails = () => {
    setEditWebsiteOpen(false);
    setDesignRailOpen(false);
    setSettingsRailOpen(false);
    setReorderPagesOpen(false);
    setEditPageOpen(false);
    setAddPageOpen(false);

    // closing the right rail should reset the preview theme
    resetThemeToCurrent();
    resetPreviewLayout();
  };

  const setRailsForIndexBanner = () => {
    closeAllRails();
    setEditWebsiteOpen(true);
    setSettingsRailOpen(true);
  };

  useEffect(() => {
    setPagesLoaded(true);

    // open design rail if sent from wws manager
    if (railOpen) {
      toggleEditWebsiteHandler();
      toggleChangeThemeRailHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // If we update the preview theme, and that does not have an associated paper suite image
    // Move away from the paper preview.
    if (!paperSuiteImageId && selectedPreview === 'paper') {
      setSelectedPreview('desktop');
    } else if (selectedPreview === 'paper') {
      // close all rails when switching to paper
      closeAllRails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paperSuiteImageId, selectedPreview]);

  // construct preview url
  const themeParam = previewThemeId ? `&themeId=${String(previewThemeId)}` : '';
  const layoutParam = previewLayout ? `&viewSinglePage=${previewLayout === 'single-page'}` : '';

  const flipperOverrideParams = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const overrides: string[] = [];
    params.forEach((value, key) => {
      if (key.startsWith('flipper_override')) {
        overrides.push(`&${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    });
    return overrides.join('');
  }, []);

  // Uncomment to test locally with wws-guest-view running
  // const previewUrlBase = 'http://qa-dev.theknot.com:3001';
  const previewUrlBase = process.env.REACT_APP_PREVIEW_URL_BASE || '';

  const previewUrl = `${previewUrlBase}/us/${wwsData?.weddingWebsite?.slug}/${previewLocation}?preview=true&isInEditMode=true${themeParam}${layoutParam}${flipperOverrideParams}`;

  const toggleEditWebsiteHandler = () => {
    if (editWebsiteOpen) {
      closeAllRails();
    } else {
      if (selectedPreview === 'paper') {
        setSelectedPreview('desktop');
      }
      setEditWebsiteOpen(true);
      track('WWS Preview Interaction', { selection: 'edit page' });
    }
    setShowErrorToast(false);
    setIsAnimating(true);
  };

  const toggleEditPageHandler = () => {
    setEditPageOpen(prevState => !prevState);
  };

  const toggleSettingsRailHandler = () => {
    setSettingsRailOpen(prevState => !prevState);
  };

  const toggleReorderPagesHandler = () => {
    setReorderPagesOpen(prevState => !prevState);
  };

  const toggleChangeThemeRailHandler = () => {
    // closing the design rail should reset the preview theme
    if (designRailOpen) {
      resetThemeToCurrent();
      resetPreviewLayout();
    }
    setDesignRailOpen(prevState => !prevState);
  };

  const toggleAddPageHandler = () => {
    setAddPageOpen(prevState => !prevState);
  };

  const toggleMobileDesktopCallback = (selectId: string) => {
    switch (selectId) {
      case 'category-inv':
        setSelectedPreview('paper');
        setEditWebsiteOpen(false);
        setRightRailOpen(false);
        break;
      case 'desktop':
        setSelectedPreview('desktop');
        break;
      case 'mobile':
        setSelectedPreview('mobile');
        break;
    }
    track('WWS Preview Interaction', { selection: selectId });
  };

  const changeThemeOrShowModal = async () => {
    const previousTheme =
      wwsData?.weddingWebsite && themes ? lookupTheme(wwsData?.weddingWebsite.themeId, themes) : null;

    if (previousTheme && !previousTheme?.active) {
      setChangeInactiveThemeModalOpen(true);
    } else {
      await setThemeToCurrentPreview();
    }
  };

  const previewThemeCallback = (themeId: number) => {
    setPreviewThemeId(themeId);
    setPreviewLocation('');
    setCurrentPage(pages.find(p => p.routeName === ''));
    history.replace(previewThemeRoute(themeId));
  };

  const setRsvpEmptyStateHandler = (state: boolean) => {
    if (state) {
      setEditWebsiteOpen(state);
    }
    setShowRsvpEmptyState(state);
  };

  const trackDesignSelection = (previousTheme: Theme | null | undefined, newThemeId: number) => {
    const newTheme = lookupTheme(newThemeId, themes || []);
    track('Design Selection', {
      ...getThemeAnalyticsBaseProps(newTheme),
      previousTheme: previousTheme?.name,
      colorwayUpdate: !!previousTheme?.family && previousTheme.family === newTheme?.family,
    });
  };

  const trackPaperClick = () => {
    const theme = lookupTheme(previewThemeId || wwsData?.weddingWebsite.themeId || 0, themes);
    track('Matching Invitation links', {
      source: 'preview',
      themeName: theme?.name,
      page: 'preview',
      partnerName: theme?.themeDesigner?.companyName,
    });
  };

  const setThemeToCurrentPreview = async () => {
    if (previewThemeId && !updatingTheme) {
      const previousTheme =
        wwsData?.weddingWebsite && themes ? lookupTheme(wwsData?.weddingWebsite.themeId, themes) : null;
      setShowErrorToast(false);

      await updateTheme({
        variables: {
          id: previewThemeId,
        },
        update(cache, { data }) {
          updateThemeSuccessCallback(cache, data, previewThemeId);
        },
      })
        .then(() => {
          trackDesignSelection(previousTheme, previewThemeId);
          resetThemeToCurrent();
        })
        .catch(() => {
          setShowErrorToast(true);
        });

      setChangeInactiveThemeModalOpen(false);
    }
  };

  // on currentPage change:
  useEffect(() => {
    // Automatically open edit page
    if (currentPage === RSVP_PAGE && isUnstartedState) {
      setEditWebsiteOpen(true);
      setShowRsvpEmptyState(true);
    }
  }, [currentPage, isUnstartedState, history]);

  // Only animate the iframe when opening or closing the right rail
  // (not when switching between mobile and desktop)
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isAnimating) {
      timeout = setTimeout(() => {
        setIsAnimating(false);
      }, 500);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isAnimating]);

  // Don't push theme preview updates of the iframe src to the browser back button
  useEffect(() => {
    iframeRef?.current?.contentWindow?.location.replace(previewUrl);
  }, [previewUrl, iframeRef]);

  const onMessage = useCallback(
    async ({ data }: MessageEvent) => {
      if (data) {
        try {
          const message = JSON.parse(data);

          if (possibleLocationQueryParamValues.includes(message.data?.routeName)) {
            setEditWebsiteOpen(true);
            setEditPageOpen(true);
          }

          if (message.type === 'EDIT_PAGE_TITLE') {
            const targetPage = pages.find(p => p.id === message.data.pageId);

            //send message to the iframe to update the current page
            iframeRef?.current?.contentWindow?.postMessage(
              JSON.stringify({
                type: 'currentPage',
                data: targetPage,
              }),
              getTargetOrigin()
            );

            //let the iframe update it's state before opening the right rail
            //so that we get the correct page when the right rail opens
            await new Promise(resolve => setTimeout(resolve, 100));
            setEditPageOpen(true);
            return;
          }
          if (message.type === 'EDIT_COVER_PHOTO') {
            //set the iframe to the home page
            iframeRef?.current?.contentWindow?.postMessage(
              JSON.stringify({
                type: 'currentPage',
                data: pages[0],
              }),
              getTargetOrigin()
            );
            setEditPageOpen(true);
            return;
          }
          // Added EDIT_COUPLE_NAME and EDIT_DATE_LOCATION to handle new message types from guest view when clicking "Edit" on couple name or date/location, which should open basic info rail just like EDIT_BASIC_INFO
          if (
            message.type === 'EDIT_BASIC_INFO' ||
            message.type === 'EDIT_COUPLE_NAME' ||
            message.type === 'EDIT_DATE_LOCATION'
          ) {
            history.push(basicInfoRailRoute(true));
            setEditPageOpen(true);
            return;
          }
          if (message.type === 'EDIT_PAGE_ITEM') {
            const editPageRoute = pageItemTypeRoute(
              message.data.pageItemType,
              message.data.pageId,
              message.data.pageItemId,
              true
            );

            //for Gif and photo items, we only route to the rail for the page the
            //item is on. For single page layouts, we need to pass the current page
            //to the iframe so that it opens the correct page rail
            const targetPage = pages.find(p => p.id === message.data.pageId);
            iframeRef?.current?.contentWindow?.postMessage(
              JSON.stringify({
                type: 'currentPage',
                data: targetPage,
              }),
              getTargetOrigin()
            );

            // if the default route is returned, don't push to history
            if (editPageRoute === previewRoute) return;
            history.push(editPageRoute);
            setEditPageOpen(true);
            return;
          }
          if (message.type === 'DELETE_PAGE_ITEM') {
            // skip event items, just open right rail
            if (message.data.pageItemType === 'EventGroupItem') {
              const editPageRoute = pageItemTypeRoute(
                message.data.pageItemType,
                message.data.pageId,
                message.data.pageItemId,
                true
              );
              history.push(editPageRoute);
              setEditPageOpen(true);
              return;
            }
            try {
              setIsDeletingPageItem(true);
              // using the deleteGif hook here, but any of the page item deletion mutation
              // hooks would work. They all call the generic deletePageItem mutation
              await deletePageItemMutation({
                variables: { attributes: { id: message.data.pageItemId, pageId: message.data.pageId } },
                update(cache) {
                  deletePageItem({ pageId: message.data.pageId, itemId: message.data.pageItemId, cache });
                },
              });
              const targetPage = pages.find(p => p.id === message.data.pageId);

              // using currentPage message so that we return the user back to the page
              // they were on after deleting the item and refreshing
              iframeRef.current?.contentWindow?.postMessage(
                JSON.stringify({
                  type: 'currentPage',
                  data: targetPage,
                }),
                getTargetOrigin()
              );
            } catch (error) {
              Honeybadger.notify(error, {
                message: 'Error deleting PageItem while using click to edit',
                context: {
                  error,
                },
              });
            } finally {
              setIsDeletingPageItem(false);
            }
            return;
          }
          if (message.data === RSVP_PAGE) {
            setCurrentPage(RSVP_PAGE);
          } else if (message.data?.__typename && message.data.__typename === 'WWS_Page') {
            // Reload page if coming from RSVP
            if (currentPage === RSVP_PAGE) {
              iframeRef.current?.contentWindow?.postMessage(
                JSON.stringify({
                  type: 'reload',
                }),
                getTargetOrigin()
              );
            }

            setCurrentPage(message.data);
          }
        } catch (error) {
          // Ignore the error?
        }
      }
    },
    [possibleLocationQueryParamValues, pages, history, deletePageItemMutation, currentPage]
  );

  const textGenerationPrimaryClick = () => {
    field?.setFieldText?.(field?.fieldName || '', generatedText);
    track('AI Button Clicked', {
      action: 'WWS AI',
      actionName: 'text replaced',
    });
    setModalOpen(false);
  };

  const textGenerationSecondaryClick = () => {
    field?.setFieldText?.(field?.fieldName || '', `${field?.fieldText} ${generatedText}`);
    track('AI Button Clicked', {
      action: 'WWS AI',
      actionName: 'text insert',
    });
    setModalOpen(false);
  };

  /*
   * Updates local state when the page in the iframe changes.
   */
  useEffect(() => {
    if (history.location.pathname !== previewRoute && !history.location.pathname.includes('basic-info')) {
      history.push(previewRoute);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onMessage, history]);

  const iframeContainerClasses = () => {
    return `${styles.iframeContainer} ${styles[selectedPreview]} ${
      editWebsiteOpen || rightRailOpen ? styles.rightRailOpen : ''
    } ${isAnimating ? styles.animating : ''} ${!!previewThemeId ? styles.iframePreviewActive : ''}`;
  };

  const showPreviewRightRail = !editPageOpen && !reorderPagesOpen && !addPageOpen && !settingsRailOpen;

  return (
    <FullScreenModal className={selectedPreview === 'paper' ? styles.paperSuiteModal : ''}>
      <div data-testid="preview-page" className={styles.previewPage}>
        <PreviewHeader
          allowPaperPreview={paperSuiteImageId ? true : false}
          selectedPreview={selectedPreview}
          changeSelectedPreview={toggleMobileDesktopCallback}
          editWebsiteToggle={toggleEditWebsiteHandler}
          className={`${styles.previewHeader} ${editWebsiteOpen || rightRailOpen ? styles.rightRailOpen : ''}`}
          showPurchaseDomain={shouldPurchaseDomain}
          setShowPublishedIndexBanner={setShowPublishedIndexBanner}
        />
        <>
          {selectedPreview === 'paper' ? (
            <div className={styles.paperSuiteContainer}>
              <div className={styles.paperSuiteContent}>
                <div className={styles.paperSuitePhotoContainer}>
                  <div className={styles.paperSuiteImg}>
                    <Picture id={paperSuiteImageId}>
                      <Img />
                    </Picture>
                  </div>
                  <div className={styles.paperSuiteTextContainer}>
                    <div className={styles.paperSuiteText}>
                      <span>
                        Get three free stationery samples that match your Wedding Website with promo code 3FREE.
                      </span>
                      <br />
                      <br />
                      <a target="_blank" onClick={trackPaperClick} rel="noopener noreferrer" href={designerUrl || ''}>
                        View Matching Designs on The Knot
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {textGenerationModalOpen && (
                <TextGenerationModal
                  isThinking={isThinking}
                  generatedText={generatedText}
                  retryRequest={isRetryableError ? retryRequest : undefined}
                  title={generatedTextModalTitle}
                  onClose={() => setModalOpen(false)}
                  onPrimaryClick={textGenerationPrimaryClick}
                  onSecondaryClick={textGenerationSecondaryClick}
                  errorMessage={errorMessage}
                  isInZeroState={isModalInZeroState}
                />
              )}
              <PublishWebsiteBanner
                setShowPublishedIndexBanner={setShowPublishedIndexBanner}
                rightRailOpen={rightRailOpen || editWebsiteOpen}
              />
              <IndexWebsiteBanner
                showPublishedIndexBanner={showPublishedIndexBanner}
                rightRailOpen={rightRailOpen || editWebsiteOpen}
                eipRailsHandler={() => setRailsForIndexBanner()}
              />
              <div className={iframeContainerClasses()}>
                {wwsLoading || isDeletingPageItem ? (
                  <Loading className={styles.previewLoadingSpinner} />
                ) : (
                  <>
                    <div className={styles.iframeSizer}>
                      <div className={styles.iframeWrap}>
                        <iframe
                          id="preview-iframe"
                          title="preview-iframe"
                          src={previewUrl}
                          className={
                            (editWebsiteOpen || rightRailOpen) && selectedPreview === 'desktop' ? styles.scaled : ''
                          }
                          ref={iframeRef}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
          <RightRail open={editPageOpen || showRsvpEmptyState} overlay={overlayEnabled} className={styles.rightRail}>
            <RightRailContext.Provider
              value={{ disableOverlay, enableOverlay, previewOnClose, previewAfterSave, closeAfterSave, iframeRef }}
            >
              {currentPage === RSVP_PAGE || showRsvpEmptyState ? (
                <ManageRsvpSettingsRail
                  preview
                  onClose={closeAllRails}
                  leftButtonOnClick={toggleEditPageHandler}
                  onSaveSuccess={() =>
                    iframeRef.current?.contentWindow?.postMessage(
                      JSON.stringify({
                        type: 'reload',
                      }),
                      getTargetOrigin()
                    )
                  }
                />
              ) : (
                <>
                  <PageItemRoutes preview onCloseHandler={closeAllRails} onBackHandler={closeAllRails}>
                    {typeof currentPage !== 'string' && (
                      <EditPageRail
                        pageId={currentPage?.id}
                        setEditPageOpen={toggleEditWebsiteHandler}
                        leftButtonOnClick={toggleEditPageHandler}
                        iframeRef={iframeRef}
                      />
                    )}
                  </PageItemRoutes>
                </>
              )}
            </RightRailContext.Provider>
          </RightRail>
          <PageSettingsRail preview className={styles.rightRail} />
          <BasicInfoRail
            className={styles.rightRail}
            iframeRef={iframeRef}
            liteSite={liteSite}
            closeRail={closeAllRails}
          />
          {!!previewThemeId && themes ? (
            <div className={styles.mobileWebPreviewFooter}>
              <ThemeInfo
                themeFamily={lookupThemeFamily(previewThemeId, themes)}
                thumbnailTheme={lookupTheme(previewThemeId, themes)}
                onClick={previewThemeCallback}
                selected={previewThemeId}
              />
              <DoubleActionWithError
                primaryOnClick={changeThemeOrShowModal}
                secondaryOnClick={() => {
                  resetThemeToCurrent(false);
                }}
                errorText="Something went wrong! Your theme has not been updated."
                showSpinner={updatingTheme}
                showError={showErrorToast}
                secondaryText="Cancel"
              />
            </div>
          ) : null}
        </>
        <>
          <RightRail open={settingsRailOpen} overlay={overlayEnabled} className={styles.rightRail}>
            <SettingsRailContent leftButtonOnClick={toggleSettingsRailHandler} onClose={closeAllRails} />
          </RightRail>
        </>
        <>
          <RightRail open={reorderPagesOpen} overlay={overlayEnabled} className={styles.rightRail}>
            <ReorderPages
              onClose={closeAllRails}
              leftButtonOnClick={toggleReorderPagesHandler}
              updatePreview={() =>
                iframeRef.current?.contentWindow?.postMessage(
                  JSON.stringify({
                    type: 'reload',
                  }),
                  getTargetOrigin()
                )
              }
            />
          </RightRail>
        </>
        <>
          <RightRail open={addPageOpen} overlay={overlayEnabled} className={styles.rightRail}>
            <AddPage
              leftButtonClick={() => setAddPageOpen(false)}
              onClose={closeAllRails}
              header="Add custom page"
              onSaveSuccess={() => {
                setAddPageOpen(false);
                reloadIframe(iframeRef);
              }}
            />
          </RightRail>
        </>
      </div>
      <>
        {showPreviewRightRail ? (
          <PreviewRightRail
            pageId={typeof currentPage !== 'string' ? currentPage?.id : currentPage}
            editSiteOpen={editWebsiteOpen}
            toggleEditWebsiteHandler={toggleEditWebsiteHandler}
            toggleEditPageHandler={toggleEditPageHandler}
            toggleSettingsRailHandler={toggleSettingsRailHandler}
            toggleReorderPagesHandler={toggleReorderPagesHandler}
            toggleChangeThemeRailHandler={toggleChangeThemeRailHandler}
            setCurrentPage={setCurrentPage}
            showRsvpEmptyState={showRsvpEmptyState ? true : false}
            setShowRsvpEmptyState={setRsvpEmptyStateHandler}
            addPageToggle={toggleAddPageHandler}
            iframeRef={iframeRef}
            togglePageOpen={togglePageOpen}
          />
        ) : null}
      </>
      {designRailOpen ? (
        <>
          <DesignRail
            onClose={toggleEditWebsiteHandler}
            onBack={toggleChangeThemeRailHandler}
            previewThemeId={previewThemeId}
            resetThemeToCurrent={resetThemeToCurrent}
            previewThemeCallback={previewThemeCallback}
            updatingTheme={updatingTheme}
            showErrorToast={showErrorToast}
            setThemeToCurrentPreview={changeThemeOrShowModal}
            wwsData={wwsData}
            onEscapeKey={toggleEditWebsiteHandler}
          />
          <ConfirmationModal
            open={changeInactiveThemeModalOpen}
            headerText="Switch designs?"
            bodyText="Your current design is being retired so if you switch, you can't go back."
            cancelText="Switch"
            confirmText="Cancel"
            onConfirm={() => setChangeInactiveThemeModalOpen(false)}
            onCancel={() => setThemeToCurrentPreview()}
          />
        </>
      ) : null}
    </FullScreenModal>
  );
};

export default Preview;
